import { z } from 'zod';

export const ServiceRoleSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  name: z.string(),
  // Mirror the write-side `.min(1)` (see `shortCode` below) so the read
  // path never surfaces an empty short code. Anything that would render a
  // chip — ProductLabel, SOW retainer card, product autocomplete trigger
  // — depends on this being non-empty; if the server ever ships an empty
  // string the response fails validation here rather than silently
  // collapsing into a "no chip" state at render time.
  shortCode: z.string().min(1),
  description: z.string().nullable(),
  internal: z.boolean(),
  active: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type ServiceRole = z.infer<typeof ServiceRoleSchema>;

const shortCode = z
  .string()
  .min(1)
  .max(20)
  .regex(/^[A-Z0-9_-]+$/, 'uppercase letters, digits, underscore, hyphen only');

const ServiceRoleBody = z.object({
  name: z.string().min(1).max(120),
  shortCode,
  description: z.string().nullable().optional(),
  active: z.boolean().optional(),
  internal: z.boolean().optional(),
});

export const CreateServiceRoleInput = ServiceRoleBody;
export type CreateServiceRoleInput = z.infer<typeof CreateServiceRoleInput>;

export const PatchServiceRoleInput = ServiceRoleBody.partial();
export type PatchServiceRoleInput = z.infer<typeof PatchServiceRoleInput>;

export interface ListServiceRolesQuery {
  q?: string;
  active?: boolean;
  internal?: boolean;
  sortField?: 'name' | 'shortCode' | 'internal' | 'active' | 'createdAt' | 'updatedAt';
  sortDirection?: 'asc' | 'desc';
  limit?: number;
  offset?: number;
}

/**
 * Export query. Single source of truth so a new list filter automatically
 * participates in export by adding one optional field here (and the
 * matching field on the backend's `exportQuerySchema`). Subset of
 * `ListServiceRolesQuery` minus pagination — exports drain the whole
 * filtered result.
 */
export type ExportServiceRolesQuery = Pick<
  ListServiceRolesQuery,
  'active' | 'internal' | 'q' | 'sortField' | 'sortDirection'
>;

/** Result of POST /api/service-roles/import — one entry per row in the
 *  uploaded file (in the same order). `outcome` reflects whether the
 *  row was created, updated by upsert-on-shortCode, or rejected. */
export type ImportServiceRoleRowOutcome = 'created' | 'updated' | 'failed';

/**
 * Structured per-row error. `field` (when present) is the column the
 * error relates to; the UI can use it to anchor the message next to
 * the offending column header. `code` is a coarse machine-readable
 * tag so future automation (e.g. an agent retrying with corrections)
 * can branch on it.
 */
export type ImportServiceRoleErrorCode =
  | 'REQUIRED'
  | 'INVALID_FORMAT'
  | 'TOO_LONG'
  | 'DUPLICATE_IN_FILE'
  | 'CONFLICT'
  | 'INTERNAL';

export interface ImportServiceRoleRowError {
  code: ImportServiceRoleErrorCode;
  message: string;
  field?: 'shortCode' | 'name' | 'description' | 'internal' | 'active';
}

export interface ImportServiceRolesRow {
  rowNumber: number;
  shortCode: string | null;
  outcome: ImportServiceRoleRowOutcome;
  errors?: ImportServiceRoleRowError[];
}

export interface ImportServiceRolesResult {
  totalRows: number;
  created: number;
  updated: number;
  failed: number;
  rows: ImportServiceRolesRow[];
}
